/**
 * LivePricesPanel - the Home hero's product panel ("Data Product" v3).
 * A believable slice of the real app: header with a LIVE pill, the NGN/USD
 * weekly close (big mono figure + amber caution chip), a soft navy area
 * chart of the recent rate, three live commodity rows with delta chips, and
 * a footer note. Clicking a commodity row focuses the panel on that
 * commodity (figure + 12-week trend in the row's color); clicking it again,
 * or the back chip, returns to NGN/USD.
 */

import React, { useState } from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import { Icon } from '@iconify/react';
import { AreaChart, Area, YAxis, ResponsiveContainer } from 'recharts';
import { useReducedMotion } from 'framer-motion';
import { useThemeMode, numericSx, radius } from '../../context/ThemeContext';
import DeltaChip from './DeltaChip';

// Row colors for the three commodity rows (ochre, green, clay - muted, data-viz safe).
const ROW_DOTS = ['#D9A514', '#1FA254', '#C05621'];

function LivePricesPanel({ fx, fxSeries = [], rows = [], footNote, footRight }) {
  const { palette } = useThemeMode();
  const reduce = useReducedMotion();

  // null = NGN/USD focus (default); an index = that commodity row's focus.
  const [selected, setSelected] = useState(null);
  const plottable = (r) => Array.isArray(r?.series) && r.series.length >= 2;
  const selRow = selected != null && plottable(rows[selected]) ? rows[selected] : null;
  const toggleRow = (i) => {
    if (!plottable(rows[i])) return;
    setSelected((cur) => (cur === i ? null : i));
  };

  const hasFx = Boolean(fx?.rate);
  const stroke = selRow ? ROW_DOTS[selected % ROW_DOTS.length] : palette.navy;
  const series = selRow ? selRow.series : fxSeries;
  const dataKey = selRow ? 'value' : 'rate';
  // Points carrying no figure are dropped before the domain is computed.
  // Math.min(null, 900) is 0, so one blank point pinned the axis floor to
  // zero and squashed the whole line into the top of the panel. Callers
  // mostly filter already; this makes the panel safe on its own terms.
  const vals = series.map((d) => d[dataKey]).filter((v) => v != null);
  const hasSeries = vals.length >= 2;
  const min = hasSeries ? Math.min(...vals) : 0;
  const max = hasSeries ? Math.max(...vals) : 0;
  const pad = hasSeries ? Math.max((max - min) * 0.2, 1) : 0;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Soft navy glow behind the panel */}
      <Box aria-hidden sx={{ position: 'absolute', inset: { xs: '-30px -8px', md: '-30px -20px' }, zIndex: 0, pointerEvents: 'none', background: 'radial-gradient(closest-side, rgba(15,39,68,0.10), transparent)', filter: 'blur(30px)' }} />

      {/* Panel */}
      <Box
        sx={{
          position: 'relative', zIndex: 1,
          bgcolor: '#fff',
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: `${radius.panel}px`,
          boxShadow: palette.shadowLg,
          overflow: 'hidden',
        }}
      >
        {/* Head */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.75, py: 2.1, borderBottom: `1px solid ${palette.cardBorder}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon icon="ph:chart-line-up" width={16} height={16} color={palette.navy} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '-0.01em', color: palette.text }}>Live prices</Typography>
              <Typography sx={{ fontSize: '0.74rem', color: palette.textMuted, fontWeight: 500 }}>
                Latest close{fx?.date ? ` · ${fx.date}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, px: 1.25, py: 0.55, borderRadius: 999, bgcolor: palette.accentBg }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.accentStrong, animation: reduce ? 'none' : 'lpBlink 1.8s ease-in-out infinite', '@keyframes lpBlink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } } }} />
            <Typography component="span" sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.accent }}>Live</Typography>
          </Box>
        </Box>

        {/* Focus block: NGN/USD by default, a commodity when its row is selected */}
        <Box sx={{ px: 2.75, pt: 2.4, pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.textSecondary }}>
              {selRow ? selRow.name : 'NGN / USD'}
            </Typography>
            {selRow ? (
              <ButtonBase
                onClick={() => setSelected(null)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  fontSize: '0.72rem', fontWeight: 700, fontFamily: 'inherit',
                  color: palette.accent, bgcolor: palette.accentBg,
                  border: `1px solid ${palette.accent}44`,
                  px: 1.1, py: 0.4, borderRadius: 999,
                  transition: 'background-color 0.15s, border-color 0.15s',
                  '&:hover': { bgcolor: `${palette.accent}22`, borderColor: palette.accent },
                  '&:focus-visible': { outline: `2px solid ${palette.accent}`, outlineOffset: 2 },
                }}
              >
                <Icon icon="ph:arrow-u-up-left" width={13} height={13} />
                Back to NGN/USD
              </ButtonBase>
            ) : (
              <Typography component="span" sx={{ ...numericSx, fontSize: '0.62rem', color: palette.textMuted, border: `1px solid ${palette.cardBorder}`, px: 0.7, py: 0.15, borderRadius: '5px' }}>FX · CBN</Typography>
            )}
          </Box>

          {selRow ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mt: 0.75 }}>
                <Typography sx={{ ...numericSx, fontSize: '2.1rem', fontWeight: 600, letterSpacing: '-0.03em', color: palette.text }}>
                  {selRow.value}
                </Typography>
                <DeltaChip value={selRow.change} decimals={1} />
              </Box>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.9, fontSize: '0.76rem', color: palette.textSecondary }}>
                <Icon icon="ph:chart-line" width={14} height={14} color={stroke} />
                {`Weekly market average, ${selRow.sub} - 12-week view.`}
              </Typography>
            </>
          ) : hasFx ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mt: 0.75 }}>
                <Typography sx={{ ...numericSx, fontSize: '2.1rem', fontWeight: 600, letterSpacing: '-0.03em', color: palette.text }}>
                  ₦{Number(fx.rate).toLocaleString()}
                </Typography>
                {fx.pct != null && <DeltaChip value={fx.pct} caution={fx.pct >= 0} decimals={1} />}
              </Box>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.9, fontSize: '0.76rem', color: palette.textSecondary }}>
                <Icon icon="ph:warning" width={14} height={14} color={palette.warning} />
                {fx.pct != null && fx.pct >= 0
                  ? 'Naira weakened vs the previous close - higher rate, read as caution.'
                  : 'Naira firmed against the dollar vs the previous close.'}
              </Typography>
            </>
          ) : (
            <>
              {/* Awaiting-feed state keeps the panel's weight: big muted figure + neutral chip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mt: 0.75 }}>
                <Typography sx={{ ...numericSx, fontSize: '2.1rem', fontWeight: 600, letterSpacing: '-0.03em', color: palette.textMuted }}>
                  ₦ -
                </Typography>
                <Box component="span" sx={{ ...numericSx, px: 0.9, py: 0.3, borderRadius: '6px', fontSize: '0.72rem', fontWeight: 500, color: palette.textSecondary, bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}` }}>
                  awaiting feed
                </Box>
              </Box>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.9, fontSize: '0.76rem', color: palette.textSecondary }}>
                <Icon icon="ph:clock" width={14} height={14} color={palette.textMuted} />
                Publishes with the daily CBN close.
              </Typography>
            </>
          )}

          {/* Chart */}
          {hasSeries ? (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ height: 150 }}>
                <ResponsiveContainer>
                  {/* key remounts on focus switch so the line redraws in the new color */}
                  <AreaChart key={selRow ? selRow.name : 'fx'} data={series} margin={{ top: 6, right: 2, bottom: 0, left: 2 }}>
                    <defs>
                      <linearGradient id="lppFx" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={stroke} stopOpacity={0.12} />
                        <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <YAxis hide domain={[min - pad, max + pad]} />
                    <Area type="monotone" dataKey={dataKey} stroke={stroke} strokeWidth={2} fill="url(#lppFx)" dot={false} isAnimationActive={!reduce} animationDuration={900} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.25, pt: 0.75 }}>
                {/* Real dates from the active series (FX is daily, commodity
                    rows are weekly), instead of assuming a weekly cadence. */}
                {(() => {
                  const fmt = (d) => {
                    const dt = new Date(d);
                    return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                  };
                  const picks = [0, Math.floor((series.length - 1) / 3), Math.floor(((series.length - 1) * 2) / 3)];
                  return [...picks.map((i) => fmt(series[i]?.date)), 'Now'].map((l, i) => (
                    <Typography key={`${l}-${i}`} component="span" sx={{ ...numericSx, fontSize: '0.62rem', color: palette.textMuted }}>{l}</Typography>
                  ));
                })()}
              </Box>
            </Box>
          ) : (
            <Box sx={{ height: 150, mt: 1.5, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box aria-hidden sx={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: `1px dashed ${palette.cardBorder}` }} />
              <Typography sx={{ position: 'relative', fontSize: '0.72rem', color: palette.textMuted, bgcolor: '#fff', px: 1.5 }}>
                Weekly trend draws here once rates are published
              </Typography>
            </Box>
          )}
        </Box>

        {/* Commodity rows - click one to plot it above */}
        <Box sx={{ borderTop: `1px solid ${palette.cardBorder}`, px: 1.25, py: 1, mt: 1.5 }}>
          {rows.map((r, i) => {
            const isSel = selected === i && selRow;
            const canPlot = plottable(r);
            return (
              <Box
                key={r.name}
                role="button"
                tabIndex={canPlot ? 0 : -1}
                aria-pressed={Boolean(isSel)}
                onClick={() => toggleRow(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleRow(i);
                  }
                }}
                sx={{
                  display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 1.75,
                  px: 1.5, py: 1.1, borderRadius: '10px',
                  cursor: canPlot ? 'pointer' : 'default',
                  userSelect: 'none',
                  transition: 'background-color 0.14s, box-shadow 0.14s',
                  bgcolor: isSel ? palette.surface : 'transparent',
                  boxShadow: isSel ? `inset 0 0 0 1px ${palette.cardBorder}` : 'none',
                  '&:hover': { bgcolor: palette.surface },
                  '&:focus-visible': { outline: `2px solid ${palette.accent}`, outlineOffset: '-2px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '3px', flex: 'none', bgcolor: ROW_DOTS[i % ROW_DOTS.length], boxShadow: isSel ? `0 0 0 3px ${ROW_DOTS[i % ROW_DOTS.length]}33` : 'none' }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: palette.text, lineHeight: 1.3 }}>{r.name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>{r.sub}</Typography>
                  </Box>
                </Box>
                <Typography sx={{ ...numericSx, fontSize: '0.88rem', fontWeight: 500, color: palette.text, textAlign: 'right' }}>
                  {r.value}
                </Typography>
                <DeltaChip value={r.change} size="sm" />
              </Box>
            );
          })}
        </Box>

        {/* Foot */}
        <Box sx={{ bgcolor: palette.surface, borderTop: `1px solid ${palette.cardBorder}`, px: 2.75, py: 1.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: '0.74rem', color: palette.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {footNote}
          </Typography>
          {footRight && (
            <Typography sx={{ ...numericSx, fontSize: '0.7rem', color: palette.textMuted, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {footRight}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default LivePricesPanel;
